import { Link } from 'react-router-dom'
import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { Accordion, type AccordionItem } from '../components/common/Accordion'
import { Button } from '../components/common/Button'
import { Icon } from '../components/common/Icon'
import { Eyebrow } from '../components/common/Eyebrow'
import { faqs, FAQ_GROUPS, faqsInGroup } from '../data/faq'
import { faqSchema, breadcrumbSchema, collectionPageSchema } from '../lib/seo'

/**
 * The site-wide FAQ, grouped by who is asking. Product-specific questions are deliberately absent:
 * each product page builds its own FAQ from its own record, so a composition or a dose is stated once,
 * beside the product it belongs to, and cannot drift out of step with the catalogue.
 */
export default function FaqPage() {
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'FAQ', path: '/faq' },
  ]

  return (
    <>
      <Seo
        title="Frequently Asked Questions"
        description="Answers about GreenbayBio — what the company is, how bio-fertilizers and biostimulants differ, what each approval covers, how to buy, and how to become a retailer or distributor."
        path="/faq"
        keywords={['GreenbayBio FAQ', 'bio fertilizer questions', 'NPOP FCO difference', 'become a retailer']}
        jsonLd={[
          collectionPageSchema({
            name: 'GreenbayBio frequently asked questions',
            description:
              'Answers about the company, its products and science, the approvals its products hold, and how to buy or partner.',
            path: '/faq',
          }),
          faqSchema(faqs.map((f) => ({ q: f.q, a: f.a }))),
          breadcrumbSchema(crumbs),
        ]}
      />
      <PageHeader
        eyebrow="Help & answers"
        title="Frequently asked questions."
        subtitle="Straight answers about the company, the science, the approvals our products hold and how to buy or partner — grounded in our product information."
      />

      <div className="container-x grid gap-12 py-14 lg:grid-cols-[1.4fr_0.6fr]">
        <div>
          {FAQ_GROUPS.map((group) => {
            const items: AccordionItem[] = faqsInGroup(group).map((f, i) => ({
              id: `${group}-${i}`.replace(/[^a-zA-Z0-9-]/g, '-'),
              title: f.q,
              content: <p className="leading-relaxed">{f.a}</p>,
              defaultOpen: i === 0,
            }))
            if (!items.length) return null
            return (
              <section key={group} className="mb-12 last:mb-0" aria-labelledby={`faq-${group.replace(/\W/g, '')}`}>
                <Eyebrow>
                  <span id={`faq-${group.replace(/\W/g, '')}`}>{group}</span>
                </Eyebrow>
                <div className="mt-4">
                  <Accordion items={items} />
                </div>
              </section>
            )
          })}

          <p className="mt-4 text-sm text-ink-500">
            Questions about a specific product — its composition, dose, storage or data sheet — are answered on
            that <Link to="/catalog" className="text-brand-700 hover:underline">product&rsquo;s own page</Link>, and the
            science behind the categories is explained in the{' '}
            <Link to="/knowledge" className="text-brand-700 hover:underline">knowledge base</Link>.
          </p>
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-2xl border border-sand-200 bg-white p-7 shadow-soft">
            <h2 className="font-display text-xl font-semibold text-ink">Still have a question?</h2>
            <p className="mt-2 text-sm text-ink-500">Our team is happy to help with product or agronomic questions.</p>
            <div className="mt-5 flex flex-col gap-3">
              <Button to="/contact" variant="primary" size="md">
                Contact us <Icon name="ArrowRight" className="h-4 w-4" />
              </Button>
              <Button to="/crop-solutions" variant="outline" size="md">
                Find products by crop
              </Button>
              <Button to="/resources" variant="outline" size="md">
                Data sheets &amp; approvals
              </Button>
            </div>
          </div>
        </aside>
      </div>
    </>
  )
}
